import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Stethoscope, Loader2, Check, AlertTriangle, ShieldCheck, ArrowRight,
} from "lucide-react";
import { firstName } from "@/lib/utils";
import CustomerVideoUpload from "@/components/CustomerVideoUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicQuestionnairePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [consentData, setConsentData] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/jobs/${token}/questionnaire`)
      .then(r => {
        if (cancelled) return;
        setData(r.data);
        setAnswers(r.data.answers || {});
        setConsentData(!!r.data.gdpr?.data_processing);
        setConsentMarketing(!!r.data.gdpr?.marketing);
        setDone(!!r.data.submitted_at);
      })
      .catch(() => toast.error("Couldn't load the form"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [token]);

  const set = (id, v) => setAnswers((p) => ({ ...p, [id]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data) return;
    const missing = data.questions.filter(q => q.required && !((answers[q.id] || "").trim()));
    if (missing.length) {
      toast.error(`Please answer: ${missing[0].label}`);
      return;
    }
    if (!consentData) {
      toast.error("Please tick the consent box so we can send you the video health check.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/jobs/${token}/questionnaire`, {
        answers,
        consent_data_processing: consentData,
        consent_marketing: consentMarketing,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit — please try again");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground">Form not available</div>;

  if (!data.active) {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-5">
        <div className="max-w-md card-default p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-warning mx-auto" />
          <h1 className="font-display font-bold text-2xl mt-4">Form not active</h1>
          <p className="text-sm text-muted-foreground mt-2">Your booking doesn't currently require a pre-work questionnaire. If you think this is wrong, please call us directly.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background" data-testid="questionnaire-done">
        <header className="px-5 py-5 bg-card border-b border-border">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Stethoscope className="w-5 h-5 text-primary" />
            <div className="font-display font-bold text-lg">{data.workshop_name || "Workshop"}</div>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-5 py-10">
          <div className="card-default p-8 text-center bg-success/5 border-success/30">
            <div className="w-14 h-14 rounded-full bg-success text-success-foreground grid place-items-center mx-auto">
              <Check className="w-7 h-7" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight mt-5">Thank you — got it.</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Your answers are with the technician now. They'll have everything they need to <span className="text-primary font-semibold">fix it right first time</span> when your {data.vehicle} arrives{data.appointment_time ? ` at ${data.appointment_time}` : ""}.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="questionnaire-page">
      <header className="px-5 py-5 bg-card border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/15 grid place-items-center">
              <Stethoscope className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">{data.workshop_name || "Workshop"}</div>
              <div className="text-xs text-muted-foreground">Pre-work questionnaire</div>
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div className="font-mono-plex">{(data.reg || "").toUpperCase()}</div>
            {data.appointment_time && <div>{data.appointment_time}</div>}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="card-default p-5 mb-6 bg-primary/5 border-primary/30 flex items-start gap-3" data-testid="questionnaire-intro">
          <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-bold text-lg">Help us fix it right first time.</div>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Hi {firstName(data.customer_name)} — these {data.questions.length} short questions take about 2 minutes. The more we know before your {data.vehicle} arrives, the more time we spend solving rather than searching.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="questionnaire-form">
          {data.questions.map((q, i) => (
            <div key={q.id} className="card-default p-5">
              <label className="flex items-baseline gap-2">
                <span className="font-mono-plex text-xs text-primary font-bold">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-semibold leading-snug">{q.label} {q.required && <span className="text-destructive">*</span>}</span>
              </label>
              {q.type === "select" ? (
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {q.options.map((opt) => (
                    <label key={opt} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${answers[q.id] === opt ? "border-primary bg-primary/10" : "border-border hover:bg-muted/30"}`} data-testid={`q-${q.id}-${opt.replace(/\s+/g, "-")}`}>
                      <input
                        type="radio"
                        name={q.id}
                        value={opt}
                        checked={answers[q.id] === opt}
                        onChange={(e) => set(q.id, e.target.value)}
                        className="accent-primary"
                      />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <textarea
                  value={answers[q.id] || ""}
                  onChange={(e) => set(q.id, e.target.value)}
                  rows={2}
                  placeholder="Your answer…"
                  className="input-default text-sm mt-3"
                  data-testid={`q-${q.id}-input`}
                />
              )}
            </div>
          ))}

          <CustomerVideoUpload token={token} customerFirstName={firstName(data.customer_name)} />

          <div className="card-default p-5 bg-muted/20" data-testid="questionnaire-consent">
            <div className="flex items-start gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Consent — required by UK GDPR</div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-muted/30 transition-colors" data-testid="consent-data-row">
              <input
                type="checkbox"
                checked={consentData}
                onChange={(e) => setConsentData(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                data-testid="consent-data-checkbox"
              />
              <span className="text-sm leading-snug">
                <span className="text-destructive font-semibold">*</span> {data.gdpr?.data_processing_text || "I'm happy to receive the video health check by SMS and email for today's service."}
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-muted/30 transition-colors mt-1" data-testid="consent-marketing-row">
              <input
                type="checkbox"
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                data-testid="consent-marketing-checkbox"
              />
              <span className="text-sm text-muted-foreground leading-snug">
                {data.gdpr?.marketing_text || "Optional — I'd like service / MOT reminders in future."}
              </span>
            </label>
          </div>

          <div className="sticky bottom-4 z-10">
            <button type="submit" disabled={submitting} className="btn-primary w-full text-base" data-testid="questionnaire-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Send to the technician
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
